import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../config/supabase';
import { isNativeApp } from '../utils/platform';
import { createLogger } from '../utils/logger';

const logger = createLogger('DeepLinkAuth');

/**
 * Listens for deep link callbacks from OAuth on native platforms.
 *
 * When com.wallstreetle.app://auth/callback?code=XYZ fires:
 * 1. Closes the system browser
 * 2. Extracts the authorization code from the URL
 * 3. Exchanges it for a Supabase session (PKCE code_verifier is in localStorage)
 * 4. Navigates to /
 */
export function useDeepLinkAuth(): void {
  const navigate = useNavigate();

  useEffect(() => {
    if (!isNativeApp()) return;

    let removeListener: (() => void) | null = null;

    (async () => {
      const { App } = await import('@capacitor/app');

      const handle = await App.addListener('appUrlOpen', async ({ url }) => {
        logger.info('Deep link received', { url });

        // Only handle our auth callback scheme
        if (!url.startsWith('com.wallstreetle.app://auth/callback')) return;

        // Close the system browser that was opened for OAuth
        try {
          const { Browser } = await import('@capacitor/browser');
          await Browser.close();
          // Small delay to ensure browser is fully dismissed before exchanging code
          await new Promise((r) => setTimeout(r, 300));
        } catch {
          // Browser may already be closed
        }

        // Extract code param — use string parsing because URL constructor
        // doesn't reliably parse custom schemes
        const queryString = url.split('?')[1];
        if (!queryString) {
          logger.error('No query string in deep link URL');
          return;
        }

        const params = new URLSearchParams(queryString);
        const code = params.get('code');

        if (!code) {
          logger.error('No code parameter in deep link URL');
          return;
        }

        try {
          const { error } = await supabase.auth.exchangeCodeForSession(code);
          if (error) {
            logger.error('Failed to exchange code for session', { message: error.message });
            return;
          }
          logger.info('Session established via deep link');
          navigate('/', { replace: true });
        } catch (err) {
          logger.error('Error exchanging code for session', {
            message: err instanceof Error ? err.message : 'Unknown error',
          });
        }
      });

      removeListener = () => handle.remove();
    })();

    return () => {
      removeListener?.();
    };
  }, [navigate]);
}
